import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ChevronRightIcon } from '@heroicons/react/solid';

interface SolutionCardProps {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  tier: 'bronze' | 'silver' | 'gold';
  challenges: string[];
  sectors: string[];
  productUrl?: string;
}

const SolutionCard: React.FC<SolutionCardProps> = ({
  id,
  title,
  description,
  icon,
  tier,
  challenges,
  sectors,
  productUrl,
}) => {
  // Map tier prop to semantic background/foreground colors
  const tierStyles = {
    bronze: 'bg-amber-600/10 text-amber-600 border border-amber-600/20', 
    silver: 'bg-slate-500/10 text-slate-500 border border-slate-500/20', 
    gold: 'bg-accent/10 text-accent border border-accent/20 dark:bg-dark-accent/10 dark:text-dark-accent dark:border-dark-accent/20',
  };

  const tierLabels = {
    bronze: 'Bronze',
    silver: 'Silver',
    gold: 'Gold',
  };

  const isSaaS = !!productUrl;
  const saasStatus = id === 'meetingmaster' ? 'Live' : 'Beta';

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      whileHover={{ y: -5, boxShadow: '0 10px 20px rgba(0,0,0,0.1)' }}
      className="flex flex-col h-full bg-card dark:bg-dark-card rounded-xl overflow-hidden shadow-soft dark:shadow-dark-card group border border-border/40 dark:border-dark-border/40"
    >
      <div className="p-6 flex-grow">
        <div className="flex items-start justify-between">
          <div className="h-12 w-12 rounded-lg bg-primary/10 dark:bg-dark-accent/10 flex items-center justify-center text-primary dark:text-dark-accent">
            {icon}
          </div>
          <div className="flex gap-2">
            {isSaaS && (
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                saasStatus === 'Live' 
                  ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20' 
                  : 'bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 border border-indigo-500/20'
              }`}>
                {saasStatus}
              </span>
            )}
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${tierStyles[tier]}`}>
              {tierLabels[tier]}
            </span>
          </div>
        </div>
        
        <h3 className="mt-4 text-xl font-semibold text-primary dark:text-dark-accent">{title}</h3>
        <p className="mt-2 text-muted-foreground dark:text-dark-muted-foreground text-sm font-light leading-relaxed">{description}</p>
        
        <div className="mt-4">
          <h4 className="text-xs font-semibold uppercase tracking-wider text-card-foreground dark:text-dark-card-foreground/80">Addresses Challenges:</h4>
          <div className="mt-2 flex flex-wrap gap-1">
            {challenges.map((challenge, index) => (
              <span key={index} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/5 text-primary/80 dark:bg-dark-primary/10 dark:text-dark-primary-foreground/80 border border-border/30 dark:border-dark-border/30">
                {challenge}
              </span>
            ))}
          </div>
        </div>
        
        <div className="mt-4">
          <h4 className="text-xs font-semibold uppercase tracking-wider text-card-foreground dark:text-dark-card-foreground/80">Target Sectors:</h4>
          <div className="mt-2 flex flex-wrap gap-1">
            {sectors.map((sector, index) => (
              <span key={index} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-accent/5 text-accent/80 dark:bg-dark-accent/10 dark:text-dark-accent border border-accent/10 dark:border-dark-accent/10">
                {sector}
              </span>
            ))}
          </div>
        </div>
      </div>
      
      <div className="border-t border-border dark:border-dark-border p-4 flex items-center justify-between bg-secondary/20 dark:bg-dark-secondary/10">
        <Link 
          to={`/solutions/${id}`} 
          className="flex items-center text-foreground/75 hover:text-accent dark:text-dark-foreground/75 dark:hover:text-dark-accent font-semibold text-xs uppercase tracking-wider"
        >
          <span>Details</span>
          <ChevronRightIcon className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
        </Link>
        
        {productUrl && (
          <a 
            href={productUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-4 py-1.5 rounded-full text-xs font-semibold bg-accent text-white hover:bg-accent/90 transition-colors shadow-soft"
          >
            <span>Launch App</span>
            <svg className="ml-1.5 h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        )}
      </div>
    </motion.div>
  );
};

export default SolutionCard;
